<p-toast [style]="{marginTop: '80px'}"></p-toast>
@if (formLoading) {
  <div class="progress-spinner">
    <app-spinner [formLoading]="formLoading"></app-spinner>
  </div>
}

<div class="promotions-form" [formGroup]="form">
  <!-- FRAME 1: Header -->
  <h2>VRBO Promotions</h2>
  <p class="text-muted mb-4">Create and manage property promotions on Vrbo</p>

  <!-- FRAME 2: Property Selection (hidden for WIKI_COUPON — uses Apply to All Properties toggle in Coupon Details) -->
  @if (form.value.promotionType !== 'WIKI_COUPON') {
  <p-fieldset legend="Properties" styleClass="p-fluid" [toggleable]="false">
    <!-- Selection Mode Toggle -->
    <div class="mb-3 p-fluid-reset">
      <label class="font-bold mb-2 block"
        pTooltip="Choose how to select properties for this promotion. 'By Unit' lets you pick individual properties one by one. 'By Development' selects all active properties under one or more resorts/complexes (e.g. Honua Kai, Kaanapali Alii). 'By Location' selects all active properties in an entire geographic area (e.g. Maui, Park City). For bulk selections, a confirmation dialog will appear before creation."
        tooltipPosition="top">
        Selection Mode <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
      </label>
      <p-selectButton [options]="selectionModes" formControlName="selectionMode"
        (onChange)="onSelectionModeChange()" [style]="{'min-width': '450px'}">
      </p-selectButton>
    </div>

    <!-- Mode A: By Unit -->
    @if (form.value.selectionMode === 'BY_UNIT') {
      <div class="mb-3">
        <label class="font-bold mb-2 block"
          pTooltip="Select one or more individual properties. You can search by property code (e.g. KBM-1234) or VRBO listing ID. Use the search box to filter. Selected properties appear as chips below the dropdown. Only active properties are shown. Each selected property will have the promotion created separately via the VRBO API."
          tooltipPosition="top">
          Units <span class="p-error">*</span> <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
        </label>
        <p-multiSelect [options]="allProperties" formControlName="selectedUnits"
          optionLabel="displayLabel" optionValue="shortTermRentalId"
          [filter]="true" filterBy="propertyCode,vrboId"
          placeholder="Select units..." [showToggleAll]="true"
          display="chip" [maxSelectedLabels]="3"
          selectedItemsLabel="{0} units selected"
          (onChange)="onUnitSelectionChange()">
        </p-multiSelect>
      </div>
    }

    <!-- Mode B: By Development -->
    @if (form.value.selectionMode === 'BY_DEVELOPMENT') {
      <div>
        <div class="grid">
          <div class="col-12 md:col-6 mb-3">
            <label class="font-bold mb-2 block"
              pTooltip="Optionally narrow the developments dropdown by location. For example, selecting 'Maui' will only show Maui-based developments like Honua Kai, Kaanapali Alii, etc. Selecting 'Park City' will show Park City developments. Leave blank or clear to show all developments across all locations."
              tooltipPosition="top">
              Location (optional filter) <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
            </label>
            <p-dropdown [options]="locations" formControlName="selectedLocation"
              optionLabel="label" optionValue="locationId"
              [filter]="true" [showClear]="true"
              placeholder="All locations"
              (onChange)="onLocationFilterChange()">
            </p-dropdown>
          </div>
          <div class="col-12 md:col-6 mb-3">
            <label class="font-bold mb-2 block"
              pTooltip="Select one or more resort developments (e.g. Honua Kai, Kapalua Bay Villas). The system will automatically fetch all active properties belonging to the selected developments from the server. The resolved properties will appear in the preview below. You can remove individual properties from the resolved list if needed."
              tooltipPosition="top">
              Developments <span class="p-error">*</span> <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
            </label>
            <p-multiSelect [options]="filteredDevelopments" formControlName="selectedDevelopments"
              optionLabel="adrDevelopmentName" optionValue="developmentId"
              optionDisabled="_noDisable"
              [filter]="true" filterBy="adrDevelopmentName"
              placeholder="Select developments..." [showToggleAll]="true"
              display="chip" [maxSelectedLabels]="3"
              selectedItemsLabel="{0} developments selected"
              (onChange)="onDevelopmentSelectionChange()">
            </p-multiSelect>
          </div>
        </div>
      </div>
    }

    <!-- Mode C: By Location -->
    @if (form.value.selectionMode === 'BY_LOCATION') {
      <div class="mb-3">
        <label class="font-bold mb-2 block"
          pTooltip="Select one or more geographic locations (e.g. Maui, Park City). The system will automatically fetch all active properties in those locations from the server. This is the broadest selection mode -- all properties across all developments in the selected locations will be included. You can remove individual properties from the resolved list if needed."
          tooltipPosition="top">
          Locations <span class="p-error">*</span> <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
        </label>
        <p-multiSelect [options]="locations" formControlName="selectedLocations"
          optionLabel="label" optionValue="locationId"
          [filter]="true" filterBy="label"
          placeholder="Select locations..." [showToggleAll]="true"
          display="chip" [maxSelectedLabels]="3"
          selectedItemsLabel="{0} locations selected"
          (onChange)="onLocationSelectionChange()">
        </p-multiSelect>
      </div>
    }

    <!-- Resolved Properties Preview -->
    @if (resolvedCount > 0) {
      <div class="resolved-preview p-3 border-round">
        <div class="flex justify-content-between align-items-center cursor-pointer" (click)="showPreview = !showPreview">
          <span class="font-bold">Selected: {{resolvedCount}} properties</span>
          <span class="preview-link">
            {{showPreview ? 'Hide' : 'Preview List'}}
            <i class="pi" [ngClass]="showPreview ? 'pi-chevron-up' : 'pi-chevron-down'" style="margin-left:4px;"></i>
          </span>
        </div>
        @if (showPreview) {
          <div class="resolved-list mt-2">
            @for (prop of resolvedProperties; track prop; let i = $index) {
              <div class="resolved-item flex justify-content-between align-items-center py-1 px-2">
                <span>{{prop.propertyCode}} — STR: {{prop.shortTermRentalId}}@if (prop.vrboId && prop.vrboId !== prop.shortTermRentalId) {
                  <span> / VRBO: {{prop.vrboId}}</span>
                }</span>
                <i class="pi pi-times cursor-pointer text-red-500" (click)="removeResolvedProperty(i)"
                pTooltip="Remove this property from the promotion. The promotion will not be created for this property. You can re-add it by changing the selection above." tooltipPosition="left"></i>
              </div>
            }
          </div>
        }
      </div>
    }
  </p-fieldset>
  }

  <!-- FRAME 3: Promotion Type -->
  <p-fieldset legend="Promotion Type" styleClass="p-fluid mt-3" [toggleable]="false">
    <div class="mb-3">
      <label class="font-bold mb-2 block"
        pTooltip="Choose the VRBO promotion type. Each type has unique rules: Early Booking (book 60-500 days ahead), Last Minute (book 1-30 days before), New Listing (90-day onboarding window only), Member Only (tiered loyalty discounts for Blue/Silver/Gold members), Mobile Only (mobile app/web bookings only). Hover over each option in the dropdown for full details."
        tooltipPosition="top">
        Type <span class="p-error">*</span> <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
      </label>
      <p-dropdown [options]="promotionTypes" formControlName="promotionType"
        optionLabel="label" optionValue="value"
        placeholder="Select promotion type"
        (onChange)="onPromotionTypeChange()">
        <ng-template let-item pTemplate="item">
          <div [pTooltip]="promotionTooltips[item.value]" tooltipPosition="right" class="w-full">
            {{item.label}}
          </div>
        </ng-template>
      </p-dropdown>
    </div>
    <!-- Promotion Type Description -->
    @if (form.value.promotionType) {
      <div class="promotion-type-info p-3 border-round mt-2"
        style="background-color: #e8f4fd; border-left: 4px solid #2196F3; white-space: pre-line; font-size: 0.9rem; line-height: 1.5; color: #333;">
        <i class="pi pi-info-circle mr-2" style="color: #2196F3;"></i>
        {{promotionTooltips[form.value.promotionType]}}
      </div>
    }
  </p-fieldset>

  <!-- WIKI COUPON specific fields -->
  @if (form.value.promotionType === 'WIKI_COUPON') {
    <p-fieldset legend="Coupon Details" styleClass="p-fluid mt-3" [toggleable]="false">
      <div class="grid">
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block">
            Coupon Code <span class="p-error">*</span>
          </label>
          <input pInputText formControlName="couponCode" placeholder="e.g. VACATION20"
            style="text-transform: uppercase;" />
        </div>
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block">Coupon Name</label>
          <input pInputText formControlName="couponName" placeholder="Display name (optional)" />
        </div>
      </div>
      <div class="grid">
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block">
            Discount Value <span class="p-error">*</span>
          </label>
          <p-inputNumber formControlName="discountValue" mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="2"
            placeholder="e.g. 20 for 20%">
          </p-inputNumber>
        </div>
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block">Discount Unit</label>
          <p-dropdown [options]="discountUnits" formControlName="discountUnit"
            optionLabel="label" optionValue="value">
          </p-dropdown>
        </div>
      </div>
      <div class="grid">
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block">
            Booking Window Start <span class="p-error">*</span>
          </label>
          <p-calendar formControlName="bookingWindowFrom" dateFormat="mm/dd/yy" [showIcon]="true"
            placeholder="Coupon available from"></p-calendar>
        </div>
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block">
            Booking Window End <span class="p-error">*</span>
          </label>
          <p-calendar formControlName="bookingWindowTo" dateFormat="mm/dd/yy" [showIcon]="true"
            placeholder="Coupon available until"></p-calendar>
        </div>
      </div>
      <div class="grid">
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block">Travel Date From</label>
          <p-calendar formControlName="travelDateFrom" dateFormat="mm/dd/yy" [showIcon]="true"
            placeholder="Check-in eligible from"></p-calendar>
        </div>
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block">Travel Date To</label>
          <p-calendar formControlName="travelDateTo" dateFormat="mm/dd/yy" [showIcon]="true"
            placeholder="Check-in eligible until"></p-calendar>
        </div>
      </div>
      <div class="grid">
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block">Auto-Applied</label>
          <p-inputSwitch formControlName="isAutoApplied"></p-inputSwitch>
          <small class="text-muted ml-2">Applies automatically without guest entering code</small>
        </div>
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block">Apply to All Properties</label>
          <p-inputSwitch formControlName="applyToAllProperties"></p-inputSwitch>
          <small class="text-muted ml-2">Uncheck to restrict to selected properties</small>
        </div>
      </div>
    </p-fieldset>
  }

  <!-- FRAME 4A: Single Discount (Early Booking, Last Minute, Mobile Only) -->
  @if (isDiscountRequired) {
    <p-fieldset legend="Discount" styleClass="p-fluid mt-3" [toggleable]="false">
      @if (isDayOfWeekEligible) {
        <div class="mb-3">
          <label class="font-bold mb-2 block"
            pTooltip="Choose Single to apply one discount to all booking days, or Day-of-Week to set different discounts for each weekday (Monday to Sunday)."
            tooltipPosition="top">
            Discount Mode <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
          </label>
          <p-selectButton [options]="discountModes" [(ngModel)]="selectedDiscountMode" [ngModelOptions]="{standalone:true}"
            (onChange)="onDiscountModeChange()">
          </p-selectButton>
        </div>
      }
      @if (selectedDiscountMode === 'SINGLE') {
        <div class="grid">
          <div class="col-12 md:col-6 mb-3">
            <label class="font-bold mb-2 block"
              pTooltip="Choose the discount unit type. PERCENT: a percentage off the nightly rate (e.g. 15% off means the guest pays 85% of the original price). AMOUNT: a fixed dollar amount subtracted from the nightly rate (e.g. $25 off per night). The unit applies uniformly across all selected properties."
              tooltipPosition="top">
              Discount Unit <span class="p-error">*</span> <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
            </label>
            <p-dropdown [options]="discountUnits" formControlName="discountUnit"
              optionLabel="label" optionValue="value">
            </p-dropdown>
          </div>
          <div class="col-12 md:col-6 mb-3">
            <label class="font-bold mb-2 block"
              pTooltip="Enter the discount value. For PERCENT: valid range is 1 to 75 (VRBO rejects values outside this range). For AMOUNT: must be greater than 0 (e.g. 25 for $25 off per night). Decimal values are supported (e.g. 12.5 for 12.5%). This value is sent directly to the VRBO GraphQL API as the discount amount."
              tooltipPosition="top">
              Discount Value <span class="p-error">*</span> <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
            </label>
            <p-inputNumber formControlName="discountValue" mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="2"
              placeholder="Enter value">
            </p-inputNumber>
            @if (form.value.discountUnit === 'PERCENT') {
              <small class="text-muted">Range: 1 - 75</small>
            }
            @if (form.value.discountUnit === 'AMOUNT') {
              <small class="text-muted">Must be greater than 0</small>
            }
          </div>
        </div>
      }
      @if (selectedDiscountMode === 'DAY_OF_WEEK') {
        <div>
          <p-table [value]="dayOfWeekRows" styleClass="p-datatable-sm"
            pTooltip="Configure weekday-specific discounts. All seven days are required when Day-of-Week mode is selected."
            tooltipPosition="top">
            <ng-template pTemplate="header">
              <tr>
                <th style="width:180px;">Day</th>
                <th style="width:180px;">Unit</th>
                <th>Value</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-dayRow>
              <tr>
                <td><strong>{{dayRow.dayLabel}}</strong></td>
                <td>
                  <p-dropdown [options]="discountUnits" [(ngModel)]="dayRow.unit" [ngModelOptions]="{standalone:true}"
                    optionLabel="label" optionValue="value" [style]="{'width':'100%'}"
                    pTooltip="Choose PERCENT or AMOUNT for this day." tooltipPosition="top">
                  </p-dropdown>
                </td>
                <td>
                  <p-inputNumber [(ngModel)]="dayRow.value" [ngModelOptions]="{standalone:true}"
                    mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="2"
                    [style]="{'width':'100%'}"
                    pTooltip="Discount value for this weekday. PERCENT must be between 1 and 75; AMOUNT must be greater than 0."
                    tooltipPosition="top">
                  </p-inputNumber>
                </td>
              </tr>
            </ng-template>
          </p-table>
          <small class="text-muted">All 7 days are required in Day-of-Week mode.</small>
        </div>
      }
    </p-fieldset>
  }

  <!-- FRAME 4B: Optional Discount (New Listing) -->
  @if (isNewListing) {
    <p-fieldset legend="Discount" styleClass="p-fluid mt-3" [toggleable]="false">
      <p-message severity="info" text="Eligibility is verified automatically before creation. Each property must be within its first 90 days on VRBO (onboarding window). If a property has exceeded 90 days, the VRBO API will reject the promotion for that property. The valid booking date range is determined by VRBO and aligned with the remaining onboarding window."></p-message>
      <div class="mt-3 mb-3">
        <label class="font-bold mb-2 block"
          pTooltip="Choose Single to use one discount (or default 20% when not overridden), or Day-of-Week to set weekday-specific discounts."
          tooltipPosition="top">
          Discount Mode <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
        </label>
        <p-selectButton [options]="discountModes" [(ngModel)]="selectedDiscountMode" [ngModelOptions]="{standalone:true}"
          (onChange)="onDiscountModeChange()">
        </p-selectButton>
      </div>
      @if (selectedDiscountMode === 'SINGLE') {
        <div class="mt-3 mb-3">
          <p-checkbox [(ngModel)]="overrideDiscount" [ngModelOptions]="{standalone:true}" [binary]="true"
            label="Override default 20% discount"
            pTooltip="VRBO automatically applies a 20% discount for new listing promotions. Check this box only if you want to use a different discount value (e.g. 25% or a flat $30). If unchecked, the standard 20% off will be used. Most new listings perform well with the default 20%."
            tooltipPosition="top">
          </p-checkbox>
        </div>
      }
      @if (selectedDiscountMode === 'SINGLE' && overrideDiscount) {
        <div class="grid">
          <div class="col-12 md:col-6 mb-3">
            <label class="font-bold mb-2 block"
              pTooltip="Choose the discount unit for the custom new listing discount. PERCENT: a percentage off the nightly rate. AMOUNT: a fixed dollar amount off per night."
              tooltipPosition="top">
              Discount Unit <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
            </label>
            <p-dropdown [options]="discountUnits" formControlName="discountUnit"
              optionLabel="label" optionValue="value">
            </p-dropdown>
          </div>
          <div class="col-12 md:col-6 mb-3">
            <label class="font-bold mb-2 block"
              pTooltip="Enter a custom discount value to replace the default 20%. For PERCENT: valid range 1-75. For AMOUNT: must be greater than 0. This overrides the standard new listing discount and is sent to the VRBO API."
              tooltipPosition="top">
              Discount Value <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
            </label>
            <p-inputNumber formControlName="discountValue" mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="2"
              placeholder="Enter value">
            </p-inputNumber>
          </div>
        </div>
      }
      @if (selectedDiscountMode === 'DAY_OF_WEEK') {
        <div class="mt-3">
          <p-table [value]="dayOfWeekRows" styleClass="p-datatable-sm"
            pTooltip="Configure weekday-specific discounts for New Listing. All seven days are required in this mode."
            tooltipPosition="top">
            <ng-template pTemplate="header">
              <tr>
                <th style="width:180px;">Day</th>
                <th style="width:180px;">Unit</th>
                <th>Value</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-dayRow>
              <tr>
                <td><strong>{{dayRow.dayLabel}}</strong></td>
                <td>
                  <p-dropdown [options]="discountUnits" [(ngModel)]="dayRow.unit" [ngModelOptions]="{standalone:true}"
                    optionLabel="label" optionValue="value" [style]="{'width':'100%'}"
                    pTooltip="Choose PERCENT or AMOUNT for this day." tooltipPosition="top">
                  </p-dropdown>
                </td>
                <td>
                  <p-inputNumber [(ngModel)]="dayRow.value" [ngModelOptions]="{standalone:true}"
                    mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="2"
                    [style]="{'width':'100%'}"
                    pTooltip="Discount value for this weekday. PERCENT must be between 1 and 75; AMOUNT must be greater than 0."
                    tooltipPosition="top">
                  </p-inputNumber>
                </td>
              </tr>
            </ng-template>
          </p-table>
          <small class="text-muted">Day-of-Week mode overrides default 20% and requires all 7 days.</small>
        </div>
      }
    </p-fieldset>
  }

  <!-- FRAME 4C: Tiered Discount (Member Only) -->
  @if (isMemberOnly) {
    <p-fieldset legend="Member Discount Tiers" styleClass="p-fluid mt-3" [toggleable]="false">
      <div class="mb-3">
        <label class="font-bold mb-2 block"
          pTooltip="Choose how the member-only discount is structured. Flat (SINGLE_DISCOUNT): each tier gets a straightforward percentage or dollar amount off the nightly rate -- simple and easy for guests to understand. Multi-Night (MULTI_NIGHT_DISCOUNT): the discount applies to a specific night number (e.g. the 3rd night is free or discounted). Multi-Night requires setting the Applicable Night and optionally enabling Recurring."
          tooltipPosition="top">
          Discount Type <span class="p-error">*</span> <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
        </label>
        <p-selectButton [options]="discountTypes" [(ngModel)]="selectedDiscountType" [ngModelOptions]="{standalone: true}">
        </p-selectButton>
      </div>
      <!-- Tier Table -->
      <p-table [value]="memberTiers" styleClass="p-datatable-sm"
        pTooltip="Configure the discount for each VRBO loyalty membership tier independently. Higher tiers (Gold) typically get larger discounts to reward loyalty. Each tier can use a different unit (PERCENT vs AMOUNT) and value. All three tiers must have a value set."
        tooltipPosition="top">
        <ng-template pTemplate="header">
          <tr>
            <th style="width:120px;" pTooltip="VRBO loyalty membership level. Blue is the entry-level tier, Silver is mid-tier, and Gold is the highest tier with the most benefits. Travelers see the discount for their specific tier when browsing listings." tooltipPosition="top">Tier</th>
            <th style="width:160px;" pTooltip="Choose PERCENT for a percentage off the nightly rate (e.g. 15% off), or AMOUNT for a fixed dollar discount per night (e.g. $20 off). Each tier can have a different unit." tooltipPosition="top">Unit</th>
            <th pTooltip="Enter the discount value for this tier. For PERCENT: 1-75. For AMOUNT: greater than 0. Typical setup: Blue 10%, Silver 15%, Gold 20%. Higher-tier members expect better discounts." tooltipPosition="top">Value</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-tier let-i="rowIndex">
          <tr>
            <td><strong>{{tier.tierLabel}}</strong></td>
            <td>
              <p-dropdown [options]="discountUnits" [(ngModel)]="tier.unit" [ngModelOptions]="{standalone:true}"
                optionLabel="label" optionValue="value" [style]="{'width':'100%'}">
              </p-dropdown>
            </td>
            <td>
              <p-inputNumber [(ngModel)]="tier.value" [ngModelOptions]="{standalone:true}"
                mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="2"
                [style]="{'width':'100%'}">
              </p-inputNumber>
            </td>
          </tr>
        </ng-template>
      </p-table>
      <!-- Multi-Night Options -->
      @if (selectedDiscountType === 'MULTI_NIGHT') {
        <div class="grid mt-3">
          <div class="col-12 md:col-6 mb-3">
            <label class="font-bold mb-2 block"
              pTooltip="The specific night number that receives the discount. Must be 2 or greater. For example: set to 3 and the 3rd night of the stay gets the discount. Set to 5 and the 5th night gets the discount. The guest must stay at least this many nights to receive the discount. Important: the minimum stay duration (Min Nights) must be >= this value, otherwise VRBO will reject the promotion."
              tooltipPosition="top">
              Applicable Night <span class="p-error">*</span> <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
            </label>
            <p-inputNumber formControlName="applicableNight" [min]="2" placeholder="e.g. 3">
            </p-inputNumber>
            <small class="text-muted">Discount applies to the Nth night</small>
          </div>
          <div class="col-12 md:col-6 mb-3">
            <label class="font-bold mb-2 block"
              pTooltip="Controls whether the multi-night discount repeats. When ON: the discount repeats every Nth night (e.g. if Applicable Night is 3, nights 3, 6, 9, 12... are all discounted -- great for 'every 3rd night free' offers). When OFF: only the first occurrence is discounted (e.g. only the 3rd night, regardless of how long the stay is)."
              tooltipPosition="top">
              Recurring <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
            </label>
            <p-inputSwitch formControlName="recurring"></p-inputSwitch>
            <small class="text-muted ml-2">Repeat every Nth night</small>
          </div>
        </div>
      }
    </p-fieldset>
  }

  <!-- Only show remaining sections if promotion type is selected (not for WIKI_COUPON — those fields are in the Coupon Details section) -->
  @if (form.value.promotionType && form.value.promotionType !== 'WIKI_COUPON') {
    <!-- FRAME 5: Status -->
    <p-fieldset legend="Status" styleClass="p-fluid mt-3" [toggleable]="false">
      <div class="mb-3">
        <label class="font-bold mb-2 block"
          pTooltip="Controls whether the promotion is live on VRBO. ACTIVE: the promotion is immediately visible to travelers searching for properties -- they will see the discounted price on the listing. INACTIVE: the promotion is created in the VRBO system but not shown to travelers. You can activate it later via the VRBO promotions update API. Use INACTIVE to pre-configure promotions before a launch date."
          tooltipPosition="top">
          Status <span class="p-error">*</span> <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
        </label>
        <p-dropdown [options]="statusOptions" formControlName="status"
          optionLabel="label" optionValue="value">
        </p-dropdown>
      </div>
    </p-fieldset>
    <!-- FRAME 6: Travel Date Range -->
    <p-fieldset legend="Travel Dates" styleClass="p-fluid mt-3" [toggleable]="false">
      <div class="grid">
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block"
            pTooltip="The start of the travel window -- the earliest date a guest can check in and still receive the promotion discount. For example, if set to Jun 1, 2026, only stays starting on or after Jun 1 qualify. This is the 'travelDateFrom' field in the VRBO GraphQL API. Must be today or a future date. Format: YYYY-MM-DD."
            tooltipPosition="top">
            Travel From <span class="p-error">*</span> <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
          </label>
          <p-calendar formControlName="travelDateFrom" dateFormat="yy-mm-dd"
            [showIcon]="true" [minDate]="today" placeholder="Select date">
          </p-calendar>
        </div>
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block"
            pTooltip="The end of the travel window -- the latest date a guest's stay can end (check-out) and still receive the promotion discount. For Early Booking, this cannot exceed 2 years from today. Use the 'Evergreen' option for open-ended promotions (sets date to 2079-06-06)."
            tooltipPosition="top">
            Travel To <span class="p-error">*</span> <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
          </label>
          @if (form.value.promotionType === 'EARLY_BOOKING') {
            <div class="flex align-items-center gap-2 mb-2">
              <p-inputSwitch [(ngModel)]="isEvergreen" [ngModelOptions]="{standalone: true}" (onChange)="toggleEvergreen()"
                inputId="evergreenToggle">
              </p-inputSwitch>
              <label for="evergreenToggle" class="cursor-pointer"
                pTooltip="Evergreen promotions have no real expiry. Sets travel end date to 2079-06-06 as required by VRBO for open-ended promotions." tooltipPosition="top">
                Evergreen <span class="text-muted" style="font-size:0.8rem;">(no expiry — sets date to 2079-06-06)</span>
              </label>
            </div>
            @if (isEvergreen) {
              <div class="p-inputtext p-component p-filled" style="background:#f8f9fa; color:#6c757d; padding:0.5rem 0.75rem; border-radius:6px; display:flex; align-items:center; gap:0.5rem;">
                <i class="pi pi-infinity"></i>
                <span>2079-06-06 (Evergreen)</span>
              </div>
            } @else {
              <p-calendar formControlName="travelDateTo" dateFormat="yy-mm-dd"
                [showIcon]="true" placeholder="Select date (max 2 years from today)"
                [maxDate]="maxTravelToDate">
              </p-calendar>
              <small class="text-muted" style="display:block; margin-top:4px;">
                Max: {{ maxTravelToDate | date:'yyyy-MM-dd' }}. Use Evergreen for open-ended promotions.
              </small>
            }
          } @else {
            <p-calendar formControlName="travelDateTo" dateFormat="yy-mm-dd"
              [showIcon]="true" placeholder="Select date">
            </p-calendar>
          }
        </div>
      </div>
    </p-fieldset>
    <!-- FRAME 7: Advance Booking Days -->
    <p-fieldset legend="Advance Booking Window" styleClass="p-fluid mt-3" [toggleable]="false">
      <div class="grid">
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block"
            pTooltip="The minimum number of days before the check-in date that the traveler must book to qualify for the promotion. For Early Booking: typically 60 days (VRBO minimum is 60, maximum is 500). For Last Minute: typically 1 day (VRBO range is 1-30). For other types this is optional. This is the 'bookingDateRange.min' in the VRBO API. Example: Min Days = 90 means the guest must book at least 90 days before arrival."
            tooltipPosition="top">
            Min Days
            @if (isAdvanceBookingRequired) {
              <span class="p-error">*</span>
            }
            <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
          </label>
          <p-inputNumber formControlName="advanceBookingMin" [min]="1" placeholder="Min days">
          </p-inputNumber>
        </div>
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block"
            pTooltip="The maximum number of days before the check-in date that a booking qualifies for the promotion. For Early Booking: up to 500 days (allows very early planners). For Last Minute: up to 30 days (limits to close-to-arrival bookings). This is the 'bookingDateRange.max' in the VRBO API. Example: Max Days = 500 with Min Days = 60 means the guest can book anywhere from 60 to 500 days before arrival."
            tooltipPosition="top">
            Max Days
            @if (isAdvanceBookingRequired) {
              <span class="p-error">*</span>
            }
            <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
          </label>
          <p-inputNumber formControlName="advanceBookingMax" placeholder="Max days">
          </p-inputNumber>
        </div>
      </div>
      @if (form.value.promotionType === 'EARLY_BOOKING') {
        <small class="text-muted">Book 60 to 500 days in advance</small>
      }
      @if (form.value.promotionType === 'LAST_MINUTE') {
        <small class="text-muted">Book 1 to 30 days before check-in</small>
      }
      @if (!isAdvanceBookingRequired) {
        <small class="text-muted">Optional. Leave blank for no restriction.</small>
      }
    </p-fieldset>
    <!-- FRAME 8: Stay Duration -->
    <p-fieldset legend="Stay Duration" styleClass="p-fluid mt-3" [toggleable]="false">
      <div class="grid">
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block"
            pTooltip="The minimum number of nights the guest must book to qualify for the promotion. Default is 1 (any length of stay). For Member Only with Multi-Night discount, this MUST be >= the Applicable Night value (e.g. if Applicable Night is 3, Min Nights must be at least 3). This is the 'minLengthOfStay' field in the VRBO GraphQL API. Example: Min Nights = 3 means only stays of 3+ nights qualify."
            tooltipPosition="top">
            Min Nights <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
          </label>
          <p-inputNumber formControlName="stayDurationMin" [min]="1" placeholder="1">
          </p-inputNumber>
        </div>
        <div class="col-12 md:col-6 mb-3">
          <label class="font-bold mb-2 block"
            pTooltip="The maximum number of nights for the promotion to apply. Default is 180 days (approximately 6 months). Stays longer than this value will not receive the promotion discount. This is the 'maxLengthOfStay' field in the VRBO GraphQL API. Leave at 180 for most promotions unless you want to limit the discount to shorter stays."
            tooltipPosition="top">
            Max Nights <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
          </label>
          <p-inputNumber formControlName="stayDurationMax" placeholder="180">
          </p-inputNumber>
        </div>
      </div>
      @if (isMemberOnly && selectedDiscountType === 'MULTI_NIGHT') {
        <small class="p-error">
          Min stay must be >= applicable night ({{form.value.applicableNight}})
        </small>
      }
    </p-fieldset>
    <!-- FRAME 9: Blackout Dates -->
    <p-fieldset legend="Blackout Dates" styleClass="p-fluid mt-3" [toggleable]="false">
      <label class="font-bold mb-2 block"
        pTooltip="Blackout periods are date ranges during which the promotion will NOT apply, even if the guest's stay falls within the travel date window. Use these to protect high-demand periods like holidays (Christmas, New Year), spring break, or special events where you don't want to offer discounts. Multiple blackout ranges can be added. Format: From (start of blackout) to To (end of blackout). These are sent as 'blackoutDateRanges' in the VRBO GraphQL API."
        tooltipPosition="top">
        Blackout Periods <i class="pi pi-info-circle text-muted" style="font-size:0.85rem;"></i>
      </label>
      @for (bd of blackoutDates; track bd; let i = $index) {
        <div class="grid mb-2 align-items-center">
          <div class="col-5">
            <p-calendar [(ngModel)]="bd.from" [ngModelOptions]="{standalone:true}"
              dateFormat="yy-mm-dd" [showIcon]="true" placeholder="From"
              pTooltip="The first day of the blackout period. Promotion will not apply for stays that overlap with this date range. Format: YYYY-MM-DD." tooltipPosition="top">
            </p-calendar>
          </div>
          <div class="col-5">
            <p-calendar [(ngModel)]="bd.to" [ngModelOptions]="{standalone:true}"
              dateFormat="yy-mm-dd" [showIcon]="true" placeholder="To"
              pTooltip="The last day of the blackout period. The promotion resumes for stays after this date. Must be on or after the From date. Format: YYYY-MM-DD." tooltipPosition="top">
            </p-calendar>
          </div>
          <div class="col-2">
            <button pButton type="button" icon="pi pi-trash" class="p-button-danger p-button-text"
              (click)="removeBlackoutDate(i)"
              pTooltip="Remove this blackout date range. The promotion will apply during this period again once removed." tooltipPosition="left">
            </button>
          </div>
        </div>
      }
      <button pButton type="button" label="Add Blackout Date" icon="pi pi-plus"
        class="p-button-outlined p-button-sm mt-2" (click)="addBlackoutDate()"
        pTooltip="Add a new blackout date range. You can add multiple ranges to exclude several periods (e.g. Christmas week AND Spring Break). Each range is independent." tooltipPosition="right">
      </button>
    </p-fieldset>
  }

  <!-- FRAME 10: Actions (shown for all promotion types) -->
  @if (form.value.promotionType) {
    <div class="flex justify-content-end gap-2 mt-4 mb-4">
      <button pButton type="button" label="Reset" class="p-button-secondary p-button-outlined"
        (click)="onReset()"
        pTooltip="Clear all form fields and reset the form to its initial state. This removes all selected properties, resets the promotion type, discount values, dates, and blackout periods. Use this to start fresh without reloading the page." tooltipPosition="top">
      </button>
      <button pButton type="button" [label]="isEditMode ? 'Update Promotion' : 'Create Promotion'" class="p-button-primary"
        (click)="onSubmit()" [disabled]="formLoading"
        pTooltip="Validate all inputs and submit the promotion to the VRBO API via the backend. For a single property, creation happens immediately. For multiple properties, a bulk confirmation dialog appears showing all selected properties -- you can review, deselect individual properties, and then start the bulk creation process which creates promotions sequentially with real-time status updates." tooltipPosition="top">
      </button>
    </div>
  }
</div>

<!-- FRAME 11: Bulk Confirmation Dialog -->
<p-dialog header="Confirm Bulk Promotion Creation" [(visible)]="showConfirmDialog"
  [modal]="true" [closable]="true" [style]="{width: '640px'}" [draggable]="false">

  <p class="mb-3">
    You are about to create a <strong>{{getPromotionTypeLabel()}}</strong> promotion
    for <strong>{{selectedBulkCount}}</strong> properties.
  </p>

  <div class="summary-card p-3 border-round mb-3">
    <div class="grid">
      <div class="col-4 font-bold">Type:</div>
      <div class="col-8">{{getPromotionTypeLabel()}}</div>
      @if (!isMemberOnly) {
        <div class="col-4 font-bold">Discount:</div>
      }
      @if (!isMemberOnly) {
        <div class="col-8">
          {{form.value.discountValue || '20'}}{{form.value.discountUnit === 'PERCENT' ? '%' : ''}}
          ({{form.value.discountUnit || 'PERCENT'}})
        </div>
      }
      <div class="col-4 font-bold">Travel:</div>
      <div class="col-8">
        {{form.value.travelDateFrom | date:'mediumDate'}} - {{form.value.travelDateTo | date:'mediumDate'}}
      </div>
    </div>
  </div>

  <p-table [value]="bulkProperties" [scrollable]="true" scrollHeight="300px" styleClass="p-datatable-sm">
    <ng-template pTemplate="header">
      <tr>
        <th style="width:40px;"><p-checkbox (onChange)="toggleSelectAll($event.checked)" [ngModel]="selectedBulkCount === bulkProperties.length" [binary]="true"></p-checkbox></th>
        <th>Property</th>
        <th style="width:140px;">STR ID</th>
        <th style="width:80px;">Status</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-prop>
      <tr>
        <td><p-checkbox [(ngModel)]="prop.selected" [binary]="true"></p-checkbox></td>
        <td>{{prop.propertyCode}}</td>
        <td>{{prop.shortTermRentalId}}</td>
        <td>@if (!prop.selected) {
          <span class="text-muted">skip</span>
        }</td>
      </tr>
    </ng-template>
  </p-table>

  <div class="flex justify-content-between align-items-center mt-3">
    <div>
      <a class="cursor-pointer mr-3" (click)="toggleSelectAll(true)">Select All</a>
      <a class="cursor-pointer" (click)="toggleSelectAll(false)">Deselect All</a>
    </div>
    <span class="text-muted">{{selectedBulkCount}} of {{bulkProperties.length}} selected</span>
  </div>

  <ng-template pTemplate="footer">
    <button pButton type="button" label="Cancel" class="p-button-text" (click)="showConfirmDialog = false"></button>
    <button pButton type="button" [label]="'Create for ' + selectedBulkCount + ' Properties'"
      class="p-button-primary" [disabled]="selectedBulkCount === 0"
      (click)="startBulkCreation()">
    </button>
  </ng-template>
</p-dialog>

<!-- FRAME 12: Bulk Progress Dialog -->
<p-dialog [header]="bulkComplete ? 'Bulk Creation Complete' : 'Creating Promotions...'"
  [(visible)]="showProgressDialog" [modal]="true" [closable]="bulkComplete"
  [style]="{width: '640px'}" [draggable]="false">

  <div class="mb-3">
    <div class="flex justify-content-between mb-2">
      <span class="text-muted">{{bulkProgress}} of {{bulkTotal}}</span>
    </div>
    <p-progressBar [value]="bulkTotal > 0 ? (bulkProgress / bulkTotal * 100) : 0" [showValue]="false"></p-progressBar>
  </div>

  @if (!bulkComplete && bulkCurrentProperty) {
    <div class="mb-3">
      <span class="text-muted">Current: <strong>{{bulkCurrentProperty}}</strong></span>
    </div>
  }

  <p-table [value]="activeBulkProperties" [scrollable]="true"
    scrollHeight="300px" styleClass="p-datatable-sm">
    <ng-template pTemplate="header">
      <tr>
        <th style="width:40px;">#</th>
        <th>Property</th>
        <th style="width:140px;">STR ID</th>
        <th style="width:120px;">Result</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-prop let-i="rowIndex">
      <tr [ngClass]="{'bg-red-50': prop.status === 'failed'}">
        <td>{{i + 1}}</td>
        <td>
          {{prop.propertyCode}}
          @if (prop.error) {
            <div class="text-sm p-error mt-1">{{prop.error}}</div>
          }
        </td>
        <td>{{prop.shortTermRentalId}}</td>
        <td>
          @if (prop.status === 'pending') {
            <span class="text-muted">--</span>
          }
          @if (prop.status === 'running') {
            <p-progressSpinner [style]="{width: '20px', height: '20px'}" strokeWidth="4"></p-progressSpinner>
          }
          @if (prop.status === 'success') {
            <span class="text-green-600"><i class="pi pi-check-circle mr-1"></i>Success</span>
          }
          @if (prop.status === 'failed') {
            <span class="p-error"><i class="pi pi-times-circle mr-1"></i>FAILED</span>
          }
        </td>
      </tr>
    </ng-template>
  </p-table>

  <div class="flex gap-3 mt-3">
    <span class="text-green-600 font-bold">Success: {{bulkSuccessCount}}</span>
    @if (bulkFailCount > 0) {
      <span class="p-error font-bold">Failed: {{bulkFailCount}}</span>
    }
  </div>

  <ng-template pTemplate="footer">
    @if (bulkComplete && bulkFailCount > 0) {
      <button pButton type="button"
        [label]="'Retry Failed (' + bulkFailCount + ')'" class="p-button-warning p-button-outlined"
        (click)="retryFailed()">
      </button>
    }
    @if (bulkComplete) {
      <button pButton type="button" label="Close" class="p-button-primary"
        (click)="closeProgressDialog()">
      </button>
    }
  </ng-template>
</p-dialog>
